import { Box, Button, Card, Badge, Flex, HStack, VStack, Text } from '@chakra-ui/react';
import { Tooltip } from '@/components/ui/tooltip';
import { Plus } from '@phosphor-icons/react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { fetchAPI } from '@/api/fetch';
import { apiAddCandidateToJob } from '@/api/services/job.service';
import { CandidateWithMatchInfo } from '@/types/Candidate';
import { Skeleton, SkeletonText } from '@/components/ui/skeleton';
import { getCandidateFullName } from '@/utils';

interface PotentialCandidatesProps {
  jobId: string;
  pipelineCandidateIds: string[];
  candidates: CandidateWithMatchInfo[];
  isLoading?: boolean;
  onCandidateAdded?: (candidateId?: string) => void;
}

export default function PotentialCandidates({
  jobId,
  pipelineCandidateIds,
  candidates,
  isLoading = false,
  onCandidateAdded
}: PotentialCandidatesProps) {
  const [addingToJob, setAddingToJob] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleAddCandidateToJob = useCallback(
    async (candidateId: string) => {
      setAddingToJob(candidateId);
      try {
        const response = await fetchAPI({
          request: () =>
            apiAddCandidateToJob({
              jobId,
              payload: { candidate_id: candidateId }
            })
        });

        if (response.success) {
          // Call the callback to refresh the parent component and pass the candidate ID
          onCandidateAdded?.(candidateId);
        } else {
          console.error('Failed to add candidate to job:', response.message);
        }
      } catch (error) {
        console.error('Failed to add candidate to job:', error);
      } finally {
        setAddingToJob(null);
      }
    },
    [jobId, onCandidateAdded]
  );

  const getLastWorkExperience = (candidate: CandidateWithMatchInfo) => {
    return candidate.resume_data?.work_experiences?.[0] || null;
  };

  if (isLoading) {
    return (
      <VStack align="flex-start" gap="24px">
        <SkeletonText noOfLines={1} w="200px" />
        <VStack align="flex-start" gap="16px" w="100%">
          {[1, 2, 3].map((index) => (
            <Card.Root
              key={index}
              bg="neutrals.50"
              borderRadius="12px"
              p="20px"
              w="100%"
              boxShadow="sm"
            >
              <Flex align="center" gap="40px">
                <VStack alignItems="flex-start">
                  <SkeletonText noOfLines={1} w="40px" />
                  <Skeleton borderRadius="full" w="60px" h="60px" />
                </VStack>
                <VStack align="flex-start" gap="8px" w="100%">
                  <SkeletonText noOfLines={2} w="80%" />
                </VStack>
              </Flex>
            </Card.Root>
          ))}
        </VStack>
      </VStack>
    );
  }

  // Filter out candidates that are already in the pipeline
  const filteredCandidates = candidates.filter(
    (candidate) => !pipelineCandidateIds.includes(candidate.candidate_id)
  );

  return (
    <VStack align="flex-start" gap="24px">
      <Text fontSize="18px" fontWeight="semibold" color="neutrals.800">
        Potential Candidates
      </Text>
      {filteredCandidates.length === 0 ? (
        <Box bg="neutrals.50" borderRadius="8px" p="32px" textAlign="center" w="100%">
          <Text fontSize="16px" color="neutrals.600" mb="8px">
            No potential candidates found for this job.
          </Text>
          <Text fontSize="14px" color="neutrals.500">
            The matching algorithm will automatically suggest candidates when suitable profiles are
            available.
          </Text>
        </Box>
      ) : (
        <VStack align="flex-start" gap="16px" w="100%">
          {filteredCandidates.map((candidate, index) => (
            <Flex key={index} w="100%" gap="24px" alignItems="center">
              <Card.Root
                bg="neutrals.50"
                borderRadius="12px"
                p="20px"
                w="100%"
                boxShadow="sm"
                _hover={{ boxShadow: 'md', cursor: 'pointer' }}
                transition="all 0.2s ease-in-out"
                onClick={() => {
                  navigate(`/candidate/${candidate.candidate_id}`);
                }}
              >
                <Flex align="center" gap="40px">
                  <VStack alignItems="flex-start">
                    <Text fontSize="12px" fontWeight="semibold" color="neutrals.700">
                      Match
                    </Text>
                    <Box
                      borderRadius="full"
                      p="16px"
                      w="60px"
                      h="60px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontSize="16px"
                      fontWeight="bold"
                      bg={
                        candidate.score >= 90
                          ? 'success.300'
                          : candidate.score >= 80
                          ? 'warning.300'
                          : 'error.300'
                      }
                    >
                      {candidate.score}%
                    </Box>
                  </VStack>
                  <VStack align="flex-start" gap="8px" w="100%">
                    <Flex justifyContent="space-between" alignItems="center" w="100%">
                      <Box>
                        <Text fontSize="18px" fontWeight="semibold" color="neutrals.900">
                          {getCandidateFullName(candidate.first_name, candidate.last_name)}
                        </Text>
                        <Text fontSize="14px" color="neutrals.700" mt="2px">
                          {getLastWorkExperience(candidate)
                            ? `${getLastWorkExperience(candidate)?.job_role} @ ${
                                getLastWorkExperience(candidate)?.company_name
                              }`
                            : candidate.email}
                        </Text>
                      </Box>
                      <HStack gap="12px">
                        <Badge
                          bg="primary.100"
                          color="primary.900"
                          px="12px"
                          py="4px"
                          borderRadius="full"
                          fontSize="12px"
                          fontWeight="semibold"
                        >
                          {candidate.unique_id}
                        </Badge>
                      </HStack>
                    </Flex>
                    {candidate.fit_positions.length > 0 && (
                      <Flex gap="8px" mt="8px" flexWrap="wrap">
                        {candidate.fit_positions.slice(0, 3).map((position, posIndex) => (
                          <Badge
                            key={posIndex}
                            bg="blue.100"
                            color="blue.800"
                            px="8px"
                            py="2px"
                            borderRadius="full"
                            fontSize="11px"
                            fontWeight="medium"
                          >
                            {position}
                          </Badge>
                        ))}
                        {candidate.fit_positions.length > 3 && (
                          <Badge
                            bg="neutrals.100"
                            color="neutrals.600"
                            px="8px"
                            py="2px"
                            borderRadius="full"
                            fontSize="11px"
                            fontWeight="medium"
                          >
                            +{candidate.fit_positions.length - 3} more
                          </Badge>
                        )}
                      </Flex>
                    )}
                  </VStack>
                </Flex>
              </Card.Root>
              <Tooltip
                openDelay={0}
                closeDelay={0}
                content="Add to the job pipeline"
                contentProps={{
                  css: {
                    '--tooltip-bg': 'white',
                    color: 'neutrals.900',
                    fontSize: '12px'
                  }
                }}
              >
                <Button
                  size="sm"
                  bg="neutrals.0"
                  color="primary.900"
                  border="1px solid"
                  borderColor="primary.900"
                  borderRadius="full"
                  w="48px"
                  h="48px"
                  _hover={{
                    bg: 'primary.900',
                    color: 'neutrals.0'
                  }}
                  loading={addingToJob === candidate.candidate_id}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddCandidateToJob(candidate.candidate_id);
                  }}
                >
                  <Plus weight="bold" size={16} />
                </Button>
              </Tooltip>
            </Flex>
          ))}
        </VStack>
      )}
    </VStack>
  );
}
